import React from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Truck, Clock, Package, MapPin } from 'lucide-react';

export default function ShippingPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 
            className="text-4xl md:text-5xl font-bold text-[#2C3E2D] mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
            data-testid="shipping-page-title"
          >
            Golf Club Shipping Costs
          </h1>
          <p className="text-[#5A6B5C] text-lg mb-12">
            At Impact Golf, we ensure your golf clubs are shipped safely and securely. Please review the shipping costs
            for different types of golf clubs below. We also offer in-store pickup for local buyers.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl border border-[#E8E4DC]">
              <div className="w-12 h-12 bg-[#4A6741] rounded-lg flex items-center justify-center mb-4">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-[#2C3E2D] mb-2">Domestic Shipping</h3>
              <p className="text-[#5A6B5C] text-sm">Trusted carriers deliver your clubs in 3-7 business days.</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-[#E8E4DC]">
              <div className="w-12 h-12 bg-[#4A6741] rounded-lg flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-[#2C3E2D] mb-2">Expedited &amp; Overnight</h3>
              <p className="text-[#5A6B5C] text-sm">Need it fast? Choose 2-day or overnight shipping at checkout.</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-[#E8E4DC]">
              <div className="w-12 h-12 bg-[#4A6741] rounded-lg flex items-center justify-center mb-4">
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-[#2C3E2D] mb-2">In-Store Pickup</h3>
              <p className="text-[#5A6B5C] text-sm">Local buyers can skip shipping entirely with free in-store pickup.</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-[#E8E4DC]">
              <div className="w-12 h-12 bg-[#4A6741] rounded-lg flex items-center justify-center mb-4">
                <Package className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-[#2C3E2D] mb-2">Tracking</h3>
              <p className="text-[#5A6B5C] text-sm">You'll receive a tracking number via email once your order ships.</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl border border-[#E8E4DC] space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Domestic Shipping Rates
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left" data-testid="shipping-rates-table">
                  <thead>
                    <tr className="border-b border-[#E8E4DC]">
                      <th className="py-3 text-sm font-bold text-[#334155] uppercase tracking-wider">Club Type</th>
                      <th className="py-3 text-sm font-bold text-[#334155] uppercase tracking-wider">Shipping Cost</th>
                      <th className="py-3 text-sm font-bold text-[#334155] uppercase tracking-wider">Estimated Delivery</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#5A6B5C]">
                    <tr className="border-b border-[#E8E4DC]">
                      <td className="py-3">Full Set of Clubs</td>
                      <td className="py-3">$60.00</td>
                      <td className="py-3">3-7 business days</td>
                    </tr>
                    <tr className="border-b border-[#E8E4DC]">
                      <td className="py-3">Irons Set</td>
                      <td className="py-3">$30.00</td>
                      <td className="py-3">3-7 business days</td>
                    </tr>
                    <tr className="border-b border-[#E8E4DC]">
                      <td className="py-3">Driver and Single Woods</td>
                      <td className="py-3">$30.00</td>
                      <td className="py-3">3-7 business days</td>
                    </tr>
                    <tr className="border-b border-[#E8E4DC]">
                      <td className="py-3">Putter and Single Irons</td>
                      <td className="py-3">$25.00</td>
                      <td className="py-3">3-7 business days</td>
                    </tr>
                    <tr className="border-b border-[#E8E4DC]">
                      <td className="py-3">Expedited (2-Day)</td>
                      <td className="py-3">$100.00</td>
                      <td className="py-3">2 business days</td>
                    </tr>
                    <tr>
                      <td className="py-3">Overnight</td>
                      <td className="py-3">$200.00</td>
                      <td className="py-3">1 business day</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[#5A6B5C] text-sm mt-4">* Free shipping on orders over $5,000.00</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Additional Shipping Information
              </h2>
              <ul className="space-y-3 text-[#5A6B5C]">
                <li className="flex items-start space-x-3">
                  <span className="text-[#D4A55A]">•</span>
                  <span><strong>Shipping Methods:</strong> We use trusted carriers to ensure the safe delivery of your golf clubs.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-[#D4A55A]">•</span>
                  <span><strong>P.O. Boxes:</strong> We do not ship to P.O. Box addresses.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-[#D4A55A]">•</span>
                  <span><strong>Expedited Shipping:</strong> Available upon request at checkout. Additional charges apply.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-[#D4A55A]">•</span>
                  <span><strong>Holiday Shipping:</strong> Shipping times may be extended during holiday seasons.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-[#D4A55A]">•</span>
                  <span><strong>In-Store Pickup:</strong> Local buyers can select free in-store pickup at checkout - no shipping cost.</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Track Your Order
              </h2>
              <p className="text-[#5A6B5C]">
                You will receive a tracking number via email once your order has shipped. You can track your shipment
                using the link provided in that email.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

